import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  DollarSign,
  Eye,
  CheckCircle,
  XCircle,
  Clock as ClockIcon,
  Star,
  FileText,
  Users,
  Cake,
  AlertTriangle
} from 'lucide-react';
import { Contract } from '../../types/contract';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';

interface ContractCardProps {
  contract: Contract;
  selected?: boolean;
  onSelect?: (contract: Contract) => void;
  onEdit?: (contract: Contract) => void;
  onView?: (contract: Contract) => void;
  onDelete?: (contract: Contract) => void;
  onGeneratePDF?: (contract: Contract) => void;
  onSendWhatsApp?: (contract: Contract) => void;
  onSendReceipt?: (contract: Contract) => void;
  onStatusChange?: (contract: Contract, status: Contract['status']) => void;
  onPaymentToggle?: (contract: Contract, paid: boolean) => void;
  className?: string;
}

const ContractCard: React.FC<ContractCardProps> = ({
  contract,
  selected = false,
  onSelect,
  onEdit,
  onView,
  onDelete,
  onGeneratePDF,
  onSendWhatsApp,
  onSendReceipt,
  onStatusChange,
  onPaymentToggle,
  className = ''
}) => {
  const navigate = useNavigate();

  const getStatusConfig = (status: Contract['status']) => {
    switch (status) {
      case 'confirmado':
        return {
          icon: CheckCircle,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          headerGradient: 'from-emerald-500 to-emerald-600',
          label: 'Confirmado'
        };
      case 'pendente':
        return {
          icon: ClockIcon,
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          headerGradient: 'from-amber-500 to-amber-600',
          label: 'Pendente'
        };
      case 'cancelado':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          headerGradient: 'from-red-500 to-red-600',
          label: 'Cancelado'
        };
      case 'orcamento':
        return {
          icon: FileText,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          headerGradient: 'from-blue-500 to-blue-600',
          label: 'Orçamento'
        };
      case 'concluido':
        return {
          icon: Star,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          headerGradient: 'from-purple-500 to-purple-600',
          label: 'Concluído'
        };
      default:
        return {
          icon: ClockIcon,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          headerGradient: 'from-slate-500 to-slate-600',
          label: 'Pendente'
        };
    }
  };

  const statusConfig = getStatusConfig(contract.status);
  const StatusIcon = statusConfig.icon;

  const getDaysUntilEvent = () => {
    if (!contract.dataFesta) return null;
    const today = new Date();
    const eventDate = new Date(contract.dataFesta);
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isThisWeek = () => {
    const daysUntil = getDaysUntilEvent();
    return daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
  };

  const isCurrentMonth = () => {
    if (!contract.dataFesta) return false;
    const today = new Date();
    const eventDate = new Date(contract.dataFesta);
    return today.getMonth() === eventDate.getMonth() && today.getFullYear() === eventDate.getFullYear();
  };

  const getPriorityLabel = () => {
    if (isThisWeek()) return 'Esta Semana';
    if (isCurrentMonth()) return 'Este Mês';
    return null;
  };

  const getPriorityColor = () => {
    if (isThisWeek()) return 'from-red-500 to-red-600';
    if (isCurrentMonth()) return 'from-amber-500 to-amber-600';
    return 'from-slate-400 to-slate-500';
  };

  const getUrgencyAlert = () => {
    const daysUntil = getDaysUntilEvent();
    if (daysUntil === null || daysUntil < 0) return null;
    
    if (daysUntil === 0) return { text: 'HOJE!', color: 'bg-red-600' };
    if (daysUntil === 1) return { text: 'AMANHÃ!', color: 'bg-red-600' };
    if (daysUntil <= 7) return { text: `Faltam ${daysUntil} dias`, color: 'bg-red-500' };
    
    return null;
  };

  const urgencyAlert = getUrgencyAlert();

  return (
    <div className={`group relative flex flex-col h-full ${className}`}>
      {/* Urgency Alert - Fora do card, no topo */}
      {urgencyAlert && (
        <div className={`absolute -top-2 left-1/2 transform -translate-x-1/2 z-10 ${urgencyAlert.color} text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1`}>
          <AlertTriangle className="w-3 h-3" />
          {urgencyAlert.text}
        </div>
      )}

      {/* Compact Square Card Design */}
      <div className={`relative bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full overflow-hidden border border-slate-200 ${
        selected 
          ? 'ring-2 ring-teal-500 shadow-teal-200/50' 
          : 'hover:ring-1 hover:ring-slate-300'
      }`}>
        
        {/* Compact Header */}
        <div className={`relative bg-gradient-to-r ${statusConfig.headerGradient} text-white p-4`}>
          {/* Top Accent Line */}
          <div className={`absolute top-0 left-0 right-0 h-0.5 bg-white/30`}></div>
          
          {/* Priority Badge */}
          {getPriorityLabel() && (
            <div className={`absolute -top-2 -right-2 px-2 py-1 bg-gradient-to-r ${getPriorityColor()} shadow-md rounded-full`}>
              <span className="text-white text-xs font-bold">{getPriorityLabel()}</span>
            </div>
          )}

          {/* Header Content */}
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3">
                {onSelect && (
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onSelect(contract)}
                    className={`w-4 h-4 text-white bg-white/20 border-white rounded focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-white/20 transition-colors`}
                  />
                )}
                <h3 className="text-lg font-bold text-white truncate leading-tight">
                  {contract.contratante}
                </h3>
              </div>
              
              {/* Birthday Info */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex items-center gap-2 text-white/90 min-w-0 flex-1">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Cake className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="font-semibold text-white truncate min-w-0">
                    {contract.nomeAniversariante}
                  </span>
                  {contract.idadeAniversariante && (
                    <span className="text-xs bg-white/20 text-white px-2 py-1 rounded-full font-medium flex-shrink-0">
                      {contract.idadeAniversariante} anos
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Status Badge - Below Header */}
        <div className={`flex items-center justify-center gap-1 py-2 text-xs font-semibold ${statusConfig.color} bg-white/10`}>
          <StatusIcon className="w-3 h-3" />
          <span>{statusConfig.label}</span>
        </div>

        {/* Compact Card Body - Only Essential Info */}
        <div className="p-4 flex-1">
          {/* Essential Information Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
              <Calendar className="w-4 h-4 text-blue-600" />
              <div className="min-w-0">
                <div className="text-xs text-blue-600 font-medium">Data</div>
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {contract.dataFesta ? formatDate(contract.dataFesta) : 'N/A'}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
              <Clock className="w-4 h-4 text-green-600" />
              <div className="min-w-0">
                <div className="text-xs text-green-600 font-medium">Horário</div>
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {contract.horarioInicioFesta || 'N/A'}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
              <Users className="w-4 h-4 text-purple-600" />
              <div className="min-w-0">
                <div className="text-xs text-purple-600 font-medium">Convidados</div>
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {contract.numeroConvidados || 'N/A'}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
              <DollarSign className="w-4 h-4 text-amber-600" />
              <div className="text-xs text-amber-600 font-medium">Valor</div>
              <div className="text-sm font-semibold text-slate-800 truncate">
                {!contract.valor ? (
                  <span className="text-blue-600">Pendente</span>
                ) : (
                  formatCurrency(contract.valor)
                )}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center py-3 mt-3">
            {!contract.valor ? (
              <>
                <div className="text-xs text-slate-500 mb-2">
                  Este contrato precisa de um orçamento
                </div>
                <button
                  onClick={() => navigate(`/admin/contratos/orcamento/${contract.id}`)}
                  className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 transition-all duration-200 border border-teal-700 rounded-full"
                >
                  <FileText className="w-4 h-4" />
                  <span>Montar Orçamento</span>
                </button>
                <button
                  onClick={() => navigate(`/admin/contratos/${contract.id}`)}
                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline mt-2"
                  title="Ver Detalhes"
                >
                  Ver Detalhes
                </button>
              </>
            ) : (
              <>
                <div className="text-xs text-slate-500 mb-2">
                  Clique para informações completas
                </div>
                <button
                  onClick={() => navigate(`/admin/contratos/${contract.id}`)}
                  className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-all duration-200 border border-slate-200 hover:border-slate-300 rounded-full"
                >
                  <Eye className="w-4 h-4" />
                  <span>Ver Detalhes</span>
                </button>
              </>
            )}
          </div>
        </div>

        
      </div>
    </div>
  );
};

export default ContractCard;
